import React, { useContext, useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AuthContext from '../context/AuthContext';

const ROLE_COLORS = {
  SUPERADMIN: '#7c3aed',
  ADMIN:      '#dc2626',
  HR:         '#d97706',
  IT:         '#2563eb',
  FACILITY:   '#0f766e',
  USER:       '#6b7280',
};

function Navbar() {
  const { user, isAuthenticated, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const dropRef = useRef(null);

  useEffect(() => {
    const close = (e) => { if (dropRef.current && !dropRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const publicPaths = ['/login', '/register', '/mfa-verify', '/forgot-password'];
  if (!isAuthenticated || publicPaths.includes(pathname) || pathname.startsWith('/reset-password') || pathname.startsWith('/sign/')) return null;

  const handleLogout = () => { setOpen(false); logout(); navigate('/login'); };
  const roleColor   = ROLE_COLORS[user?.role] || '#6b7280';
  const initials    = `${user?.firstName?.[0] || ''}${user?.lastName?.[0] || ''}`.toUpperCase();
  const fullName    = `${user?.firstName || ''} ${user?.lastName || ''}`.trim();
  const homeRoute   = ['ADMIN','IT','SUPERADMIN','HR','FACILITY'].includes(user?.role) ? '/admin' : '/dashboard';

  return (
    <nav style={{
      position: 'sticky', top: 0, zIndex: 999,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '0 28px', height: 54,
      background: '#0f172a',
      borderBottom: '2px solid #1e293b',
      boxShadow: '0 2px 12px rgba(0,0,0,0.35)',
    }}>

      {/* ── Left: logo ── */}
      <span
        onClick={() => navigate(homeRoute)}
        style={{ color: '#f1f5f9', fontWeight: 700, fontSize: '1rem', cursor: 'pointer', letterSpacing: 0.4, userSelect: 'none', display: 'flex', alignItems: 'center', gap: 8 }}
      >
        <img src="/new logo.png" alt="Logo" style={{ height: 28, width: 'auto' }} />
        HR/IT Lifecycle
      </span>

      {/* ── Right: name + initials avatar ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }} ref={dropRef}>

        {/* Full name label */}
        <span style={{ color: '#94a3b8', fontSize: '0.875rem', fontWeight: 500, letterSpacing: 0.2 }}>
          {fullName}
        </span>

        {/* Role badge */}
        <span style={{
          background: roleColor, color: '#fff',
          padding: '2px 8px', borderRadius: 4,
          fontSize: '0.7rem', fontWeight: 700, letterSpacing: 0.4,
        }}>
          {user?.role}
        </span>

        {/* Initials circle button */}
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setOpen(p => !p)}
            title="Account"
            style={{
              width: 36, height: 36, borderRadius: '50%',
              background: open ? roleColor : '#1e293b',
              border: `2px solid ${open ? roleColor : '#334155'}`,
              color: '#fff', fontWeight: 700, fontSize: '0.82rem',
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 0.15s', letterSpacing: 0.5,
            }}
            onMouseEnter={e => { if (!open) { e.currentTarget.style.background = roleColor; e.currentTarget.style.borderColor = roleColor; } }}
            onMouseLeave={e => { if (!open) { e.currentTarget.style.background = '#1e293b'; e.currentTarget.style.borderColor = '#334155'; } }}
          >
            {initials}
          </button>

          {/* Dropdown */}
          {open && (
            <div style={{
              position: 'absolute', top: 'calc(100% + 10px)', right: 0,
              background: '#1e293b', border: '1px solid #334155',
              borderRadius: 10, boxShadow: '0 12px 32px rgba(0,0,0,0.5)',
              minWidth: 250, zIndex: 1000, overflow: 'hidden',
            }}>
              {/* Header */}
              <div style={{ padding: '14px 18px', background: '#0f172a', borderBottom: '1px solid #334155', display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{
                  width: 40, height: 40, borderRadius: '50%', background: roleColor, flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#fff', fontWeight: 700, fontSize: '0.9rem',
                }}>
                  {initials}
                </div>
                <div>
                  <div style={{ color: '#f1f5f9', fontWeight: 700, fontSize: '0.95rem' }}>{fullName}</div>
                  <div style={{ background: roleColor, color: '#fff', padding: '1px 7px', borderRadius: 3, fontSize: '0.68rem', fontWeight: 700, display: 'inline-block', marginTop: 3 }}>{user?.role}</div>
                </div>
              </div>

              {/* Details */}
              <div style={{ padding: '12px 18px', borderBottom: '1px solid #334155' }}>
                {[
                  { label: 'Username',    val: user?.username },
                  { label: 'Email',       val: user?.email },
                  { label: 'Employee ID', val: user?.employeeId },
                ].filter(r => r.val).map(row => (
                  <div key={row.label} style={{ marginBottom: 10 }}>
                    <div style={{ color: '#475569', fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 1 }}>{row.label}</div>
                    <div style={{ color: '#cbd5e1', fontSize: '0.875rem', fontWeight: 500 }}>{row.val}</div>
                  </div>
                ))}
              </div>

              {/* Country / Client / Employee Docs links */}
              {['SUPERADMIN', 'COUNTRY_ADMIN', 'ADMIN'].includes(user?.role) && (
                <div style={{ padding: '8px', borderBottom: '1px solid #334155' }}>
                  {['SUPERADMIN'].includes(user?.role) && (
                    <NavItem label="🌍 Country Management" path="/admin/countries" navigate={navigate} setOpen={setOpen} />
                  )}
                  {['SUPERADMIN', 'COUNTRY_ADMIN'].includes(user?.role) && (
                    <NavItem label="🏢 Client Management" path="/admin/clients" navigate={navigate} setOpen={setOpen} />
                  )}
                  {['SUPERADMIN', 'COUNTRY_ADMIN', 'ADMIN'].includes(user?.role) && (
                    <NavItem label="📄 Employee Documents" path="/admin/employee-documents" navigate={navigate} setOpen={setOpen} />
                  )}
                </div>
              )}

              {/* Log out */}
              <div style={{ padding: '8px' }}>
                <button
                  onClick={handleLogout}
                  style={{
                    width: '100%', padding: '9px 14px',
                    background: 'transparent', border: '1px solid #3f3f46',
                    color: '#f87171', borderRadius: 6, cursor: 'pointer',
                    fontSize: '0.875rem', fontWeight: 600,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#dc2626'; e.currentTarget.style.borderColor = '#dc2626'; e.currentTarget.style.color = '#fff'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = '#3f3f46'; e.currentTarget.style.color = '#f87171'; }}
                >
                  ⏻ Log Out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
}

function NavItem({ label, path, navigate, setOpen }) {
  return (
    <button
      onClick={() => { setOpen(false); navigate(path); }}
      style={{
        width: '100%', padding: '8px 14px',
        background: 'transparent', border: 'none',
        color: '#94a3b8', borderRadius: 6, cursor: 'pointer',
        fontSize: '0.875rem', fontWeight: 500, textAlign: 'left',
        transition: 'all 0.12s',
      }}
      onMouseEnter={e => { e.currentTarget.style.background = '#0f172a'; e.currentTarget.style.color = '#f1f5f9'; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#94a3b8'; }}
    >
      {label}
    </button>
  );
}

export default Navbar;
